/**
 * pc-behavior — passive behavior collector.
 *
 * Accumulates element visibility/clicks (data-pc-obs tagged), friction
 * signals (rage clicks, dead clicks, no-fit result), and scroll depth for the
 * current pageview, then ships ONE `behavior` event through Relay when the
 * page is hidden. Load AFTER relay.js in layout/theme.liquid.
 *
 * Fails soft everywhere: analytics must never break the storefront.
 */
(function (window, document) {
  'use strict';

  if (window.PCBehavior && window.PCBehavior.__loaded) { return; }

  var state = {
    template: window.__pcTemplate || 'unknown',
    seen: {}, clicked: {}, fold: {},
    scroll: 0, rage: {}, dead: 0, nofit: false, sent: false
  };

  function keys(obj) {
    var out = [], k;
    for (k in obj) { if (obj.hasOwnProperty(k)) { out.push(k); } }
    return out;
  }

  // --- element visibility + above-the-fold ---------------------------------
  function initObserved() {
    var els = document.querySelectorAll('[data-pc-obs]');
    var vh = window.innerHeight || 0;
    var io = ('IntersectionObserver' in window)
      ? new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting && en.intersectionRatio >= 0.5) {
              state.seen[en.target.getAttribute('data-pc-obs')] = 1;
              io.unobserve(en.target);
            }
          });
        }, { threshold: 0.5 })
      : null;
    els.forEach(function (el) {
      var key = el.getAttribute('data-pc-obs');
      try {
        if (el.getBoundingClientRect().top < vh) { state.fold[key] = 1; }
      } catch (e) {}
      if (io) { io.observe(el); } else { state.seen[key] = 1; }
    });
  }

  // --- clicks: tagged elements, rage, dead ---------------------------------
  var lastClicks = [];   // [{sel, t}]
  function selectorFor(el) {
    var s = el.tagName ? el.tagName.toLowerCase() : '?';
    if (el.id) { return s + '#' + el.id; }
    if (el.classList && el.classList.length) { s += '.' + el.classList[0]; }
    return s.slice(0, 60);
  }
  function isInteractive(el) {
    while (el && el !== document.body) {
      if (el.hasAttribute && (el.hasAttribute('data-pc-obs') || el.hasAttribute('onclick'))) { return true; }
      var tag = el.tagName;
      if (tag === 'A' || tag === 'BUTTON' || tag === 'INPUT' || tag === 'SELECT' ||
          tag === 'TEXTAREA' || tag === 'LABEL' || tag === 'SUMMARY' || tag === 'VIDEO') { return true; }
      if (el.getAttribute && el.getAttribute('role') === 'button') { return true; }
      el = el.parentElement;
    }
    return false;
  }
  function onPointerDown(ev) {
    var el = ev.target;
    if (!el || !el.closest) { return; }

    var tagged = el.closest('[data-pc-obs]');
    if (tagged) { state.clicked[tagged.getAttribute('data-pc-obs')] = 1; }

    var now = Date.now();
    var sel = selectorFor(tagged || el);
    lastClicks.push({ sel: sel, t: now });
    if (lastClicks.length > 6) { lastClicks.shift(); }
    var rapid = lastClicks.filter(function (c) { return c.sel === sel && now - c.t < 1000; });
    if (rapid.length >= 3) { state.rage[sel] = 1; }

    if (!tagged && !isInteractive(el)) { state.dead++; }
  }

  // --- scroll milestones ---------------------------------------------------
  function onScroll() {
    try {
      var doc = document.documentElement;
      var max = doc.scrollHeight - window.innerHeight;
      if (max <= 0) { state.scroll = 100; return; }
      var pct = Math.round((window.pageYOffset / max) * 100);
      [25, 50, 75, 100].forEach(function (m) {
        if (pct >= m && state.scroll < m) { state.scroll = m; }
      });
    } catch (e) {}
  }

  // --- no-fit flag: wrap Relay.track once ----------------------------------
  function hookFitment() {
    if (!(window.Relay && window.Relay.track)) { return; }
    var orig = window.Relay.track;
    window.Relay.track = function (name, props) {
      try {
        if (name === 'fitment_checked' && props && props.result === 'does_not_fit') {
          state.nofit = true;
        }
      } catch (e) {}
      return orig.apply(window.Relay, arguments);
    };
  }

  // --- send once on hide ---------------------------------------------------
  function send() {
    if (state.sent || !(window.Relay && window.Relay.track)) { return; }
    state.sent = true;
    window.Relay.track('behavior', {
      template: state.template,
      seen: keys(state.seen),
      clicked: keys(state.clicked),
      fold: keys(state.fold),
      scroll: state.scroll,
      rage: keys(state.rage).slice(0, 5),
      dead: state.dead,
      nofit: state.nofit
    });
    window.Relay.flush(true);   // beacon; relay.js's own pagehide flush is a backstop
  }

  function boot() {
    initObserved();
    hookFitment();
    document.addEventListener('pointerdown', onPointerDown, { passive: true, capture: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') { send(); }
    });
    window.addEventListener('pagehide', send);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.PCBehavior = { __loaded: true };
})(window, document);
